/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import sanityClient from '../lib/sanityClient';
import EnhancedStatsTable from '../components/EnhancedStatsTable';
import { processPlayerStats, validateStatsData } from '../utils/statsProcessing';
import { GridSkeleton } from '../components/ui/LoadingSkeleton';
import ErrorBoundary from '../components/ui/ErrorBoundary';
import logger from '../utils/logger';

function StatsPage() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const [playerStats, setPlayerStats] = useState([]);
  const [originalPlayerData, setOriginalPlayerData] = useState([]);
  const [seasonStats, setSeasonStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    fetchStatsData();

    // Auto-refresh stats every 5 minutes to ensure latest data
    const interval = setInterval(
      () => {
        logger.info('Auto-refreshing stats data');
        fetchStatsData();
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearInterval(interval);
  }, [seasonId]);

  const fetchStatsData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      logger.info('Fetching enhanced player statistics data');

      const data = await sanityClient.fetch(`
        {
          "players": *[_type == "player" && (active == true || defined(currentSeasonStats.gp) && currentSeasonStats.gp > 0 || count(seasonStats[defined(gp) && gp > 0]) > 0)] | order(active desc, name asc) {
            _id,
            name,
            number,
            positionGroup,
            "imageUrl": image.asset->url,
            slug,
            captainStatus,
            canPlayGoalie,
            canPlaySkater,
            active,
            seasonStats[] {
              _key,
              season->{
                _id,
                name
              },
              statsType,
              gp,
              g,
              a,
              pts,
              pim,
              ppg,
              ppa,
              shg,
              otg,
              eng,
              sa,
              ga,
              sv,
              svp,
              gaa,
              mins
            },
            currentSeasonStats
          },
          "seasons": *[_type == "season"] | order(startDate desc) {
            _id,
            name,
            startDate,
            endDate,
            active
          },
          "games": *[_type == "event" && eventType == "game" && isComplete == true && defined(playerStats)] {
            _id,
            title,
            startDate,
            playerStats[] {
              "playerId": player._ref,
              positionPlayed,
              gamesPlayed,
              goals,
              assists,
              penaltyMinutes,
              powerPlayGoals,
              powerPlayAssists,
              shortHandedGoals,
              overtimeGoals,
              emptyNetGoals,
              shotsAgainst,
              goalsAgainst,
              saves,
              minutesPlayed
            }
          }
        }
      `);

      logger.info('Successfully fetched stats data', {
        players: data.players?.length || 0,
        seasons: data.seasons?.length || 0,
        games: data.games?.length || 0,
      });

      // Process the player data for enhanced display
      const processedPlayers = processPlayerStats(data.players, selectedSeason);

      // Validate data integrity
      const validationIssues = validateStatsData(processedPlayers);
      if (validationIssues.length > 0) {
        logger.warn('Data validation issues found', { issues: validationIssues });
      }

      setOriginalPlayerData(data.players || []);
      setPlayerStats(processedPlayers);
      setSeasons(data.seasons || []);

      // Set season based on URL param or default to current season
      let targetSeason;
      if (seasonId) {
        targetSeason = data.seasons?.find((s) => s._id === seasonId);
      } else {
        targetSeason = data.seasons?.find((s) => s.active) || data.seasons?.[0];
      }

      if (targetSeason) {
        setSelectedSeason(targetSeason);
        // Reprocess with the target season
        const reprocessedPlayers = processPlayerStats(data.players, targetSeason);
        setPlayerStats(reprocessedPlayers);

        // Calculate season-wide statistics
        const seasonStatsData = calculateSeasonStats(reprocessedPlayers, data.games);
        setSeasonStats(seasonStatsData);
      } else {
        // Use default processed players for stats calculation
        const seasonStatsData = calculateSeasonStats(processedPlayers, data.games);
        setSeasonStats(seasonStatsData);
      }
    } catch (err) {
      logger.error('Error fetching stats data', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSeasonStats = (players, games) => {
    const stats = {
      totalPlayers: players.length,
      totalGames: games.length,
      dualPositionPlayers: players.filter((p) => p.processedStats?.isDualPosition).length,
      goalies: players.filter((p) => p.processedStats?.positions.includes('goalie')).length,
      forwards: players.filter(
        (p) =>
          p.processedStats?.primaryPosition === 'forward' ||
          (p.processedStats?.primaryPosition === 'skater' && p.positionGroup !== 'defense')
      ).length,
      defense: players.filter(
        (p) => p.processedStats?.primaryPosition === 'defense' || p.positionGroup === 'defense'
      ).length,
    };

    return stats;
  };

  const handleSeasonChange = (newSeasonId) => {
    if (newSeasonId) {
      navigate(`/stats/${newSeasonId}`);
    } else {
      navigate('/stats');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="h-8 bg-gray-700 rounded w-64 mx-auto mb-4 animate-pulse"></div>
            <div className="h-4 bg-gray-700 rounded w-96 mx-auto animate-pulse"></div>
          </div>
          <GridSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Error Loading Statistics</h1>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={fetchStatsData}
              className="bg-yellow-600 hover:bg-yellow-700 text-black px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
              Player Statistics
            </h1>
            {selectedSeason && (
              <p className="text-xl text-gray-300 mb-6">{selectedSeason.name} Season</p>
            )}
          </div>

          {/* Season Selector */}
          {seasons.length > 1 && (
            <div className="mb-8 flex justify-center">
              <select
                value={selectedSeason?._id || ''}
                onChange={(e) => handleSeasonChange(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="">All Seasons</option>
                {seasons.map((season) => (
                  <option key={season._id} value={season._id}>
                    {season.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* All Players Table */}
          <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden">
            <EnhancedStatsTable
              players={playerStats}
              season={selectedSeason}
              title="All Player Statistics"
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default StatsPage;
